import React, { useState, useEffect, useCallback, useRef } from "react";
import { Navigate } from "react-router-dom";
import { Activity, RefreshCw, Cpu, Clock, Loader2 } from "lucide-react";
import { Card, CardContent } from "components/ui/card";
import api from "lib/api";
import { useAuth } from "contexts/AuthContext";
import { useI18n } from "i18n";

const fmtDur = (ms) => {
  if (ms == null) return "—";
  const s = ms / 1000;
  if (s < 60) return `${s.toFixed(s < 10 ? 1 : 0)}s`;
  const m = Math.floor(s / 60);
  return `${m}m ${Math.round(s % 60)}s`;
};

// A wait that is getting long turns amber → red.
const durClass = (ms) => (ms >= 60000 ? "text-red-500" : ms >= 20000 ? "text-amber-500" : "text-foreground");

export default function Jobs() {
  const { t } = useI18n();
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const timerRef = useRef(null);

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.get(`/api/jobs/`);
      setData(res.data || {});
    } catch { /* ignore */ } finally { if (!silent) setLoading(false); }
  }, []);

  useEffect(() => {
    load();
    timerRef.current = setInterval(() => {
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      load(true);
    }, 3000);
    return () => { if (timerRef.current) clearInterval(timerRef.current); };
  }, [load]);

  if (user && !user.capabilities?.manage_system) return <Navigate to="/liveness/sessions" replace />;

  const gate = data?.ai_gate || {};
  const calls = data?.ai_calls || [];
  const queue = data?.deep_queue || [];
  const inflight = gate.inflight || 0;
  const maxN = gate.max_concurrency || 0;

  const Stat = ({ label, value, sub, tone }) => (
    <div className="rounded-xl border border-border bg-card px-4 py-3">
      <div className="text-xs uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className={`text-2xl font-bold mt-0.5 ${tone || ""}`}>{value}</div>
      {sub && <div className="text-xs text-muted-foreground mt-0.5">{sub}</div>}
    </div>
  );

  return (
    <div className="animate-fade-in w-full">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="flex items-center gap-2">
            <Activity className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold tracking-tight">{t("jobs.title", "Jobs — AI queue")}</h1>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{t("jobs.subtitle", "Ollama concurrency, calls running now, and the deep-PAD queue. Auto-refresh 3s.")}</p>
        </div>
        <button type="button" onClick={() => load()} disabled={loading}
          className="h-10 inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3 text-sm hover:bg-muted/50 disabled:opacity-60">
          <RefreshCw className={`h-4 w-4 ${loading ? "animate-spin" : ""}`} /> {t("sessions.refresh", "Refresh")}
        </button>
      </div>

      {/* AI concurrency gate */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <Stat label={t("jobs.inflight", "AI calls in flight")} value={`${inflight} / ${maxN}`}
          tone={inflight >= maxN && maxN ? "text-amber-500" : "text-emerald-500"}
          sub={t("jobs.inflightSub", "executing vs concurrency cap")} />
        <Stat label={t("jobs.waiting", "Queued (contestant)")} value={gate.hi_waiting || 0}
          tone={(gate.hi_waiting || 0) > 0 ? "text-amber-500" : ""}
          sub={t("jobs.waitingSub", "high-priority calls waiting for a slot")} />
        <Stat label={t("jobs.padCap", "Deep PAD slots")} value={gate.pad_max_concurrency ?? "—"}
          sub={t("jobs.padCapSub", "max concurrent async PAD calls")} />
        <Stat label={t("jobs.queueLen", "Deep-PAD queue")} value={queue.length}
          tone={queue.length > 0 ? "text-amber-500" : ""}
          sub={t("jobs.queueLenSub", "sessions pending analysis")} />
      </div>

      {/* Executing AI calls */}
      <Card className="mb-6">
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b border-border flex items-center gap-2 text-sm font-semibold">
            <Cpu className="h-4 w-4 text-primary" /> {t("jobs.executing", "Executing now")}
            <span className="text-xs text-muted-foreground font-normal">({calls.length})</span>
          </div>
          {calls.length === 0 ? (
            <div className="text-center py-10 text-sm text-muted-foreground">{t("jobs.noneExecuting", "No AI call executing right now.")}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="text-left px-4 py-2 font-medium">{t("jobs.reason", "Call")}</th>
                    <th className="text-left px-4 py-2 font-medium">{t("jobs.endpoint", "Endpoint")}</th>
                    <th className="text-left px-4 py-2 font-medium">{t("jobs.model", "Model")}</th>
                    <th className="text-right px-4 py-2 font-medium">{t("jobs.waitingFor", "Waiting for response")}</th>
                  </tr>
                </thead>
                <tbody>
                  {calls.map((c, i) => (
                    <tr key={i} className="border-b border-border last:border-0">
                      <td className="px-4 py-2 font-mono text-xs">{c.reason || "—"}</td>
                      <td className="px-4 py-2 font-mono text-xs text-muted-foreground break-all">{c.endpoint || "—"}</td>
                      <td className="px-4 py-2 font-mono text-xs text-muted-foreground">{c.model || "—"}</td>
                      <td className={`px-4 py-2 text-right font-mono ${durClass(c.elapsed_ms)}`}>
                        <span className="inline-flex items-center gap-1 justify-end"><Loader2 className="h-3.5 w-3.5 animate-spin" /> {fmtDur(c.elapsed_ms)}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Deep PAD queue */}
      <Card>
        <CardContent className="p-0">
          <div className="px-4 py-3 border-b border-border flex items-center gap-2 text-sm font-semibold">
            <Clock className="h-4 w-4 text-primary" /> {t("jobs.deepQueue", "Deep-PAD queue (pending)")}
            <span className="text-xs text-muted-foreground font-normal">({queue.length})</span>
          </div>
          {queue.length === 0 ? (
            <div className="text-center py-10 text-sm text-muted-foreground">{t("jobs.queueEmpty", "Nothing pending — every session's analysis is done.")}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-border bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                    <th className="text-left px-4 py-2 font-medium">{t("jobs.session", "Session")}</th>
                    <th className="text-left px-4 py-2 font-medium">{t("jobs.company", "Company")}</th>
                    <th className="text-left px-4 py-2 font-medium">{t("jobs.subject", "Subject")}</th>
                    <th className="text-right px-4 py-2 font-medium">{t("jobs.waited", "Waiting")}</th>
                  </tr>
                </thead>
                <tbody>
                  {queue.map((q) => (
                    <tr key={q.id} className="border-b border-border last:border-0 hover:bg-muted/30">
                      <td className="px-4 py-2"><a className="font-mono text-xs text-primary hover:underline" href={`/liveness/sessions/${q.id}`}>{q.id.slice(0, 8)}…</a></td>
                      <td className="px-4 py-2 text-muted-foreground">{q.company || "—"}</td>
                      <td className="px-4 py-2 text-muted-foreground truncate max-w-[16rem]">{q.subject || "—"}</td>
                      <td className={`px-4 py-2 text-right font-mono ${durClass(q.waiting_ms)}`}>{fmtDur(q.waiting_ms)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
